import {
  Stack,
  mergeStyleSets,
  Text,
  DefaultButton,
} from "@fluentui/react";
import React from "react";
import { commonStyles } from "../CommonStyles/CommonStyles";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../utilities/AppInsights";
import appInsights, { generateCookieId } from "../../utilities/AppInsights";

const SpotLight: React.FC = () => {
  const { t } = useTranslation();

  const styles = mergeStyleSets({
    skillsContainer: {
      background: "linear-gradient(to bottom, #E4EFFF, #A1C4D7)",
      padding: "50px 0px",
      "@media (max-width: 768px)": {
        marginTop: "0px",
      },
    },
    link: {
      color: "#0074D3",
      textDecoration: "none",
      fontSize: "16px",
    },
    skillHeading: {
      fontSize: "32px",
      fontWeight: 600,
      lineHeight: "40px",
      textAlign: "left",
    },
    skillDescription: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "21.28px",
      textAlign: "left",
      marginTop: "20px",
    },
    details: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      border: "1px solid #E6F2FB",
      boxShadow: "0px 2px 4px 0px #00000024",
      backgroundColor: "white",
      padding: "10px",
      borderRadius: "24px",
      width: "100%",
      "@media (max-width: 768px)": {
        display: "block",
      },
    },
    skillsButton: {
      height: "32px",
      border: "none",
      backgroundColor: "#0078D4",
      color: "white",
      borderRadius: "8px",
      marginTop: "20px",
      padding: "20px",
      ":focus::after": {
        borderRadius: "8px !important",
        border: "2px solid black !important",
      },
    },
    skillImgMob: {
      objectFit: "cover",
      borderRadius: "16px",
      display:"none",
      "@media (max-width: 768px)": {
        display: "none"
      },
    },
    detailsDiv: {
      padding: "0 20px",
      flex: 1,
      maxWidth: "50%",
      "@media (max-width: 768px)": {
        padding: "20px 20px",
        maxWidth: "100%",
      },
    },
    logoGrid: {
      display: "grid",
      gap: "20px",
      padding: "10px 20px", // Equal horizontal padding
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      gridTemplateColumns: "repeat(1, 1fr)", // Default mobile fallback
      "@media (min-width: 480px)": {
        gridTemplateColumns: "repeat(2, 1fr)", // 2 columns on small tablets
      },
      "@media (min-width: 768px)": {
        gridTemplateColumns: "repeat(3, 1fr)", // Max 3 columns on medium+ screens
      },
    },


    logoWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      maxWidth: "150px", // Limit size to avoid overflow
      height: "100px",
      margin: "0 auto", // Center wrapper
    },

    logoItem: {
      width: "100%",
      height: "auto",
      maxHeight: "100px",
      objectFit: "contain",
    },
    logoBackground: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "cover",
      opacity: 0.05,
      zIndex: 0,
      borderRadius: "16px",
    },
    logoContainer: {
      position: "relative",
      borderRadius: "16px",
      overflow: "hidden",
      minHeight: "437px",
      width: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "@media (max-width: 768px)": {
        width: "100%",
        marginTop: "20px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "auto",
      },
    },
  });

  const spotlight = {
    heading: t("spotlight.heading"),
    description: t("spotlight.description"),
    skillsLink: t("spotlight.skillsLink"),
    buttonText: t("spotlight.assessment"),
    logos: t("spotlight.logos", { returnObjects: true }) as Array<{
      key: string;
      logo: string;
    }>,
    imageUrl: t("spotlight.imageUrl"),
  };

  const logoCount = spotlight.logos.length;

  if (
    !spotlight.heading ||
    spotlight.heading.trim().toLowerCase() === "null" ||
    !spotlight.description ||
    spotlight.description.trim().toLowerCase() === "null"
  ) {
    return null;
  }

  return (
    <Stack className={styles.skillsContainer}>
      <Stack className={commonStyles.container}>
        <Stack className={styles.details}>
          <Stack>
            <img
              src={spotlight.imageUrl}
              alt="spotlight"
              className={styles.skillImgMob}
            />
          </Stack>
          <Stack className={styles.detailsDiv}>
            <Text variant="large" className={styles.skillHeading}>
              {spotlight.heading}
            </Text>
            <Text variant="medium" className={styles.skillDescription}>
              {spotlight.description}
            </Text>
            {spotlight.buttonText &&
              spotlight.buttonText.trim().toLowerCase() !== "null" && (
                <Link
                  to={spotlight.skillsLink}
                  target="_blank"
                  tabIndex={-1}
                  onClick={() => {
                    trackEvent("SkillsAssessmentButtonClick", {
                      ButtonText: spotlight.buttonText,
                      ExternalLink: spotlight.skillsLink,
                      visitorId: generateCookieId(),
                    });
                  }}
                >
                  <DefaultButton className={styles.skillsButton}>
                    {spotlight.buttonText}
                  </DefaultButton>
                </Link>
              )}
          </Stack>

          <div className={styles.logoContainer}>
            <div
              className={styles.logoBackground}
              style={{
                backgroundImage: `url("${spotlight.imageUrl}")`,
              }}
            />
            {logoCount === 1 ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
             <div
  style={{
    maxWidth: "500px", // Bigger max width for single logo
    width: "100%",
    padding: "20px",
  }}
>
  <img
    src={spotlight.logos[0].logo}
    alt={spotlight.logos[0].key}
    style={{
      width: "100%",
      height: "auto",
      objectFit: "contain",
    }}
  />
</div>
              </div>
            ) : (
              <div className={styles.logoGrid}>
                {spotlight.logos.map((item) => (
                  <div key={item.key} className={styles.logoWrapper}>
                    <img
                      src={item.logo}
                      alt={item.key}
                      className={styles.logoItem}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SpotLight;
